import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import SponsorCard from '../components/SponsorCard';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Car, Globe, Instagram, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    instagram_handle: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/sponsors?active_only=true`);
        setSponsors(response.data);
      } catch (error) {
        toast.error('Failed to load sponsors');
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  useEffect(() => {
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/sponsor-inquiries`, formData);

      // Create mailto link
      const subject = encodeURIComponent(`Sponsor Inquiry: ${formData.company_name}`);
      const body = encodeURIComponent(
        `Name: ${formData.full_name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n` +
        `Company: ${formData.company_name}\n` +
        `Instagram: ${formData.instagram_handle || 'N/A'}\n\n` +
        `Message:\n${formData.message}`
      );
      
      window.open(`mailto:contact@revlinerally.com?subject=${subject}&body=${body}`, '_blank');

      toast.success("Thanks! We'll reach out within 48 hours. Let's build something great together.");
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        instagram_handle: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sponsors-page" className="bg-[#0A0A0A] min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-7xl text-[#F2EDE6] mb-4"
          >
            OUR RALLY FAMILY
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[#A19D98] text-lg"
          >
            These brands believe in the mission. They ride with us.
          </motion.p>
        </div>
      </section>

      {/* Current Sponsors */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-[#A19D98]">Loading sponsors...</p>
            </div>
          ) : sponsors.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#A19D98]">Be our first sponsor!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {sponsors.map((sponsor, i) => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What We Offer */}
      <section data-testid="sponsor-benefits" className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] text-center mb-16"
          >
            WHAT WE OFFER SPONSORS
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Car,
                title: 'Logo on Rally Vehicles',
                desc: 'Your brand travels every mile with us on multi-day rallies.'
              },
              {
                icon: Globe,
                title: 'Website Feature',
                desc: 'Your logo, story, and link permanently on our sponsor page.'
              },
              {
                icon: Instagram,
                title: 'Social Media Exposure',
                desc: 'Shoutouts, posts, and tags to our growing Instagram community.'
              }
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card p-8 text-center"
              >
                <benefit.icon className="w-12 h-12 text-[#E8651A] mx-auto mb-4" />
                <h3 className="font-['Oswald'] text-xl text-[#F2EDE6] mb-3">{benefit.title}</h3>
                <p className="text-[#A19D98] text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" data-testid="sponsor-contact-section" className="py-24 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] text-center mb-4">
              LET'S RIDE TOGETHER
            </h2>
            <p className="text-[#A19D98] text-center mb-12">
              Interested in sponsoring Revline Rally? Fill out the form below and we'll be in touch.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Full Name *</label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    data-testid="sponsor-name"
                    className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="sponsor-email"
                    className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    data-testid="sponsor-phone"
                    className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Company / Brand Name *</label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    data-testid="sponsor-company"
                    className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Instagram Handle</label>
                <Input
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleInputChange}
                  placeholder="@yourhandle"
                  data-testid="sponsor-instagram"
                  className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Tell us about your brand *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  required
                  data-testid="sponsor-message"
                  className="bg-[#141414] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="sponsor-submit"
                className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? 'Submitting...' : 'Send My Info'}
                <ArrowRight size={16} />
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sponsors;
